import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Popover,
  Paper,
  MenuItem,
  Grid,
  Collapse,      // ⬅️ NEW
  Divider,       // ⬅️ NEW
  useMediaQuery, // ⬅️ NEW
  useTheme,      // ⬅️ NEW
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Add } from "@mui/icons-material";
import { ExpandMore, ExpandLess } from "@mui/icons-material"; // ⬅️ NEW
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import InfiniteScroll from "react-infinite-scroll-component";

import {
  BankDetailList,
  BankDetailCreate,
  BankDetailUpdate,
  BankDetailDelete,
} from "../../../Services/Services";

const accountTypes = [
  { value: "Saving", label: "Saving Account" },
  { value: "Current", label: "Current Account" },
  { value: "Salary", label: "Salary Account" },
];

const getEmptyData = () => ({
  account_holder_name: "",
  account_number: "",
  ifsc_code: "",
  bank_name: "",
  branch_name: "",
  bank_address: "",
  account_type: "Saving",
});

const getEmptyErrors = () => ({
  account_holder_name: "",
  account_number: "",
  ifsc_code: "",
  bank_name: "",
  branch_name: "",
  bank_address: "",
  account_type: "Saving",
});

const BankDetailsManagementTable = () => {
  const [bankDetails, setBankDetails] = useState([]);
  const [formData, setFormData] = useState(getEmptyData());
  const [editMode, setEditMode] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [accountTypeFilter, setAccountTypeFilter] = useState(null);
  const [errors, setErrors] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [filters, setFilters] = useState({
    account_number: "",
    account_type: "",
  });

  // ✅ Mobile responsiveness
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [expandedBank, setExpandedBank] = useState(null);

  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(1);
      fetchBankDetails(1, false);
    }, 500);

    return () => clearTimeout(handler);
  }, [searchQuery, accountTypeFilter]);

  const handleSearchChange = (newFilters) => {
    setFilters(newFilters);
    setPage(1);
    fetchBankDetails(1, false, newFilters);
  };

  const fetchMoreData = () => {
    if (hasMore) {
      setPage((prev) => {
        const nextPage = prev + 1;
        fetchBankDetails(nextPage, true);
        return nextPage;
      });
    }
  };

  const fetchBankDetails = async (
    page = 1,
    append = false,
    customFilters = null
  ) => {
    const activeFilters = customFilters || filters;
    const data = await BankDetailList(page, activeFilters);

    if (append) {
      setBankDetails((prev) => [...prev, ...data.results]);
    } else {
      setBankDetails(data.results);
    }

    setHasMore(!!data.next);
    setPage(page);
  };

  const accountNumberExists = (accNo, excludeId = null) => {
    const list = Array.isArray(bankDetails) ? bankDetails : [];
    const target = (accNo || "").trim();
    return list.some(
      (row) =>
        (row?.account_number || "").trim() === target &&
        (excludeId ? row.id !== excludeId : true)
    );
  };

  const handleOpenAdd = () => {
    setFormData(getEmptyData());
    setEditMode(false);
    setOpenModal(true);
  };

  const handleOpenEdit = (item) => {
    setFormData(item);
    setEditingId(item.id);
    setEditMode(true);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setFormData(getEmptyData());
    setEditingId(null);
    setErrors(getEmptyErrors());
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    validateField(name, value);
  };

  const validateField = (name, value) => {
    let error = "";

    if (name === "ifsc_code") {
      if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(value.toUpperCase())) {
        error = "Invalid IFSC code (e.g. AAAA0001ABC)";
      }
    }

    if (name === "account_number") {
      if (!/^\d{9,18}$/.test(value)) {
        error = "Account number must be 9-18 digits";
      }
    }

    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const isAllFieldsFilled = (data) => {
    const requiredFields = [
      "account_holder_name",
      "account_number",
      "ifsc_code",
      "bank_name",
      "branch_name",
      "bank_address",
      "account_type",
    ];
    return requiredFields.every((field) => data[field]?.trim());
  };

  const handleDelete = (event, id) => {
    setAnchorEl(event.currentTarget);
    setDeleteId(id);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
    setDeleteId(null);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;

    const response = await BankDetailDelete(deleteId);
    if (response) {
      toast.success("Bank Detail deleted.");
      setBankDetails((prev) => prev.filter((item) => item.id !== deleteId));
    } else {
      toast.error("Failed to delete.");
    }

    handleClosePopover();
  };

  const handleSubmit = async () => {
    if (!isAllFieldsFilled(formData)) {
      toast.error("Please fill all required fields.");
      return;
    }

    if (editMode) {
      if (accountNumberExists(formData.account_number, editingId)) {
        const msg = "Account number already exists";
        toast.info(msg);
        setErrors((prev) => ({ ...prev, account_number: msg }));
        return;
      }
    } else {
      if (accountNumberExists(formData.account_number)) {
        const msg = "Account number already exists";
        toast.info(msg);
        setErrors((prev) => ({ ...prev, account_number: msg }));
        return;
      }
    }

    try {
      if (editMode) {
        await BankDetailUpdate(editingId, formData);
        toast.success("Bank Detail updated!");
      } else {
        await BankDetailCreate(formData);
        toast.success("Bank Detail added!");
      }

      handleCloseModal();
      fetchBankDetails();
    } catch (error) {
      const serverMsg =
        error?.response?.data?.account_number?.[0] ||
        error?.response?.data?.detail ||
        error?.message ||
        "Failed to save bank detail";
      toast.info(serverMsg);
      setErrors((prev) => ({ ...prev, account_number: serverMsg }));
    }
  };

  // ✅ Mobile Card Component
  const MobileBankCard = ({ bank, index }) => {
    const isExpanded = expandedBank === bank.id;

    return (
      <Box key={bank.id}>
        <Box
          onClick={() =>
            setExpandedBank(expandedBank === bank.id ? null : bank.id)
          }
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 1.5,
            cursor: "pointer",
            bgcolor: index % 2 === 0 ? "#fafafa" : "#ffffff",
            "&:active": {
              bgcolor: "#e3f2fd",
            },
            borderLeft: "4px solid #2196f3",
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <Typography sx={{ fontSize: "0.7rem", color: "#666", minWidth: 25 }}>
                #{index + 1}
              </Typography>
              <Typography sx={{ fontWeight: "bold", fontSize: "0.85rem", flex: 1 }}>
                {bank.account_holder_name}
              </Typography>
            </Box>

            <Typography
              sx={{
                fontSize: "0.72rem",
                color: "#666",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <span style={{ fontWeight: 500 }}>{bank.account_number}</span>
              <span style={{ color: "#999" }}>•</span>
              <span style={{ fontSize: "0.7rem" }}>{bank.account_type}</span>
            </Typography>
          </Box>

          <IconButton size="small" sx={{ ml: 1 }}>
            {isExpanded ? (
              <ExpandLess fontSize="small" />
            ) : (
              <ExpandMore fontSize="small" />
            )}
          </IconButton>
        </Box>

        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Box
            sx={{
              px: 1.5,
              py: 1,
              bgcolor: index % 2 === 0 ? "#f8f9fa" : "#fafafa",
              borderLeft: "4px solid #2196f3",
            }}
          >
            <Box
              sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.8, mb: 1 }}
            >
              <Box>
                <Typography
                  sx={{ fontSize: "0.6rem", color: "#999", letterSpacing: "0.5px" }}
                >
                  ACCOUNT NUMBER
                </Typography>
                <Typography sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}>
                  {bank.account_number}
                </Typography>
              </Box>

              <Box>
                <Typography
                  sx={{ fontSize: "0.6rem", color: "#999", letterSpacing: "0.5px" }}
                >
                  IFSC CODE
                </Typography>
                <Typography sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}>
                  {bank.ifsc_code}
                </Typography>
              </Box>

              <Box>
                <Typography
                  sx={{ fontSize: "0.6rem", color: "#999", letterSpacing: "0.5px" }}
                >
                  BANK NAME
                </Typography>
                <Typography sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}>
                  {bank.bank_name}
                </Typography>
              </Box>

              <Box>
                <Typography
                  sx={{ fontSize: "0.6rem", color: "#999", letterSpacing: "0.5px" }}
                >
                  ACCOUNT TYPE
                </Typography>
                <Typography sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}>
                  {bank.account_type}
                </Typography>
              </Box>

              <Box sx={{ gridColumn: "1 / -1" }}>
                <Typography
                  sx={{ fontSize: "0.6rem", color: "#999", letterSpacing: "0.5px" }}
                >
                  BRANCH
                </Typography>
                <Typography sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}>
                  {bank.branch_name}
                </Typography>
              </Box>
            </Box>

            {bank.bank_address && (
              <Box sx={{ mb: 0.8 }}>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    color: "#999",
                    letterSpacing: "0.5px",
                    mb: 0.3,
                  }}
                >
                  BANK ADDRESS
                </Typography>
                <Typography sx={{ fontSize: "0.7rem", color: "#555", lineHeight: 1.3 }}>
                  {bank.bank_address}
                </Typography>
              </Box>
            )}

            <Divider sx={{ my: 1, borderColor: "#e0e0e0" }} />

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.6 }}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenEdit(bank);
                }}
                sx={{
                  bgcolor: "#e8f5e9",
                  borderRadius: "6px",
                  width: 32,
                  height: 32,
                  "&:hover": {
                    bgcolor: "#c8e6c9",
                    transform: "scale(1.05)",
                  },
                  transition: "all 0.2s",
                }}
              >
                <EditIcon sx={{ fontSize: "1rem", color: "#4caf50" }} />
              </IconButton>

              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(e, bank.id);
                }}
                sx={{
                  bgcolor: "#ffebee",
                  borderRadius: "6px",
                  width: 32,
                  height: 32,
                  "&:hover": {
                    bgcolor: "#ffcdd2",
                    transform: "scale(1.05)",
                  },
                  transition: "all 0.2s",
                }}
              >
                <DeleteIcon sx={{ fontSize: "1rem", color: "#f44336" }} />
              </IconButton>
            </Box>
          </Box>
        </Collapse>

        <Divider />
      </Box>
    );
  };

  return (
    <Box>
      <Box>
        <Grid
          container
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          sx={{
            mt: -5,
            pt: -3,
            mb: 1,
            flexWrap: "nowrap",
          }}
        >
          <Grid item xs={12} sm={6} md={6.5}>
            <TextField
              size="small"
              fullWidth
              label="Search By Account Number"
              value={filters.account_number}
              onChange={(e) =>
                handleSearchChange({
                  ...filters,
                  account_number: e.target.value,
                })
              }
              sx={{
                "& .MuiInputBase-root": {
                  height: 30,
                  borderRadius: 0,
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.7rem",
                },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={5} md={6.5}>
            <Autocomplete
              size="small"
              fullWidth
              options={accountTypes.map((opt) => opt.value)}
              getOptionLabel={(option) =>
                accountTypes.find((x) => x.value === option)?.label || ""
              }
              value={accountTypeFilter || null}
              onChange={(e, newValue) => {
                setAccountTypeFilter(newValue);
                handleSearchChange({
                  ...filters,
                  account_type: newValue || "",
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search By Account Type"
                  sx={{
                    "& .MuiInputBase-root": {
                      height: 30,
                      borderRadius: 0,
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "0.7rem",
                    },
                  }}
                />
              )}
              isOptionEqualToValue={(option, value) => option === value}
            />
          </Grid>

          <Grid
            item
            xs={12}
            sm={2}
            md={2}
            display="flex"
            justifyContent="flex-end"
          >
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleOpenAdd}
              sx={{
                height: 35,
                minWidth: 130,
                fontSize: "0.6rem",
                borderRadius: 0,
              }}
            >
              Bank Detail
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* ✅ Conditional Rendering */}
      <InfiniteScroll
        dataLength={bankDetails.length}
        next={fetchMoreData}
        hasMore={hasMore}
        loader={<Typography textAlign="center" p={2}>Loading...</Typography>}
        height={isMobile ? "70vh" : "80vh"}
        endMessage={
          <Typography textAlign="center" p={2}>
            No more bank details
          </Typography>
        }
      >
        {isMobile ? (
          <Box sx={{ bgcolor: "background.paper", pb: 2 }}>
            {bankDetails.length > 0 ? (
              bankDetails.map((bank, index) => (
                <MobileBankCard key={bank.id} bank={bank} index={index} />
              ))
            ) : (
              <Typography textAlign="center" p={3}>
                No records found
              </Typography>
            )}
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            sx={{ overflow: "auto", maxHeight: "80vh" }}
          >
            <Table
              size="small"
              stickyHeader
              sx={{
                minWidth: { xs: 600, sm: 1000 },
                "& td, & th": {
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  borderBottom: "1px solid #ccc",
                  transition: "background-color 0.3s ease",
                },
              }}
            >
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: "#f5f5f5",
                    "& th": {
                      fontWeight: "bold",
                      fontSize: "0.7rem",
                      color: "#333",
                      borderBottom: "1px solid #999",
                      backgroundColor: "#f5f5f5",
                    },
                  }}
                >
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: 0,
                      zIndex: 3,
                      backgroundColor: "#f5f5f5",
                      minWidth: 50,
                    }}
                  >
                    S.No
                  </TableCell>
                  <TableCell>Account Holder</TableCell>
                  <TableCell>Account No</TableCell>
                  <TableCell>IFSC</TableCell>
                  <TableCell>Bank Name</TableCell>
                  <TableCell>Account Type</TableCell>
                  <TableCell
                    sx={{
                      position: "sticky",
                      right: 0,
                      zIndex: 3,
                      backgroundColor: "#f5f5f5",
                      minWidth: 80,
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {bankDetails.length > 0 ? (
                  bankDetails.map((item, index) => (
                    <TableRow
                      hover
                      key={item.id}
                      sx={{
                        backgroundColor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                        "&:hover": {
                          backgroundColor: "#e3f2fd",
                          "& td": {
                            backgroundColor: "#e3f2fd !important",
                          },
                        },
                        "& td": {
                          padding: "0px",
                          fontSize: "0.7rem",
                          fontSize: { xs: "0.75rem", sm: "0.8125rem" },
                          whiteSpace: "nowrap",
                          color: (theme) =>
                            theme.palette.mode === "dark" ? "white" : "inherit",
                          backgroundColor: (theme) =>
                            theme.palette.mode === "dark" ? "black" : "inherit",
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          position: "sticky",
                          left: 0,
                          backgroundColor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                          zIndex: 2,
                        }}
                      >
                        {index + 1}
                      </TableCell>
                      <TableCell>{item.account_holder_name}</TableCell>
                      <TableCell>{item.account_number}</TableCell>
                      <TableCell>{item.ifsc_code}</TableCell>
                      <TableCell>{item.bank_name}</TableCell>
                      <TableCell>{item.account_type}</TableCell>
                      <TableCell
                        sx={{
                          position: "sticky",
                          right: 0,
                          backgroundColor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                          zIndex: 2,
                          minWidth: 80,
                        }}
                      >
                        <IconButton
                          sx={{ padding: 0, paddingLeft: 1, paddingRight: 1 }}
                          color="primary"
                          size="small"
                          onClick={() => handleOpenEdit(item)}
                        >
                          <EditIcon transform="scale(0.6)" />
                        </IconButton>
                        <IconButton
                          sx={{ padding: 0, paddingLeft: 1, paddingRight: 1 }}
                          color="error"
                          size="small"
                          onClick={(e) => handleDelete(e, item.id)}
                        >
                          <DeleteIcon transform="scale(0.6)" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No records found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </InfiniteScroll>

      {/* Modal */}
      <Dialog open={openModal} onClose={handleCloseModal}>
        <DialogTitle>
          {editMode ? "Edit Bank Detail" : "Add Bank Detail"}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Account Holder"
            name="account_holder_name"
            value={formData.account_holder_name}
            onChange={handleChange}
            fullWidth
            margin="dense"
            required
          />
          <TextField
            label="Account Number"
            name="account_number"
            value={formData.account_number}
            onChange={handleChange}
            fullWidth
            margin="dense"
            required
            error={!!errors.account_number}
            helperText={errors.account_number}
          />

          <TextField
            label="IFSC Code"
            name="ifsc_code"
            value={formData.ifsc_code}
            onChange={handleChange}
            fullWidth
            margin="dense"
            required
            error={!!errors.ifsc_code}
            helperText={errors.ifsc_code}
          />
          <TextField
            label="Bank Name"
            name="bank_name"
            value={formData.bank_name}
            onChange={handleChange}
            fullWidth
            margin="dense"
            required
          />
          <TextField
            label="Branch Name"
            name="branch_name"
            value={formData.branch_name}
            onChange={handleChange}
            fullWidth
            margin="dense"
            required
          />
          <TextField
            label="Bank Address"
            name="bank_address"
            value={formData.bank_address}
            onChange={handleChange}
            fullWidth
            margin="dense"
            required
          />
          <TextField
            select
            label="Account Type"
            name="account_type"
            value={formData.account_type}
            onChange={handleChange}
            fullWidth
            margin="dense"
            required
          >
            {accountTypes.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="error">
            Close
          </Button>
          <Button onClick={handleSubmit} color="primary">
            {editMode ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="body2">
            Are you sure you want to delete?
          </Typography>
          <Box sx={{ mt: 1, display: "flex", gap: 1 }}>
            <Button size="small" color="error" onClick={confirmDelete}>
              Yes
            </Button>
            <Button size="small" onClick={handleClosePopover}>
              No
            </Button>
          </Box>
        </Box>
      </Popover>

      <ToastContainer />
    </Box>
  );
};

export default BankDetailsManagementTable;